import type { InstructorDiscipline } from '@features/instructors/api/types/instructor.types';

import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useOrganizations } from '@contexts/organizations-provider';
import { UploadApprovalLetterModal } from '@features/instructors/components/upload-approval-letter-modal/UploadApprovalLetterModal';
import { useApprovalLetterUpload } from '@features/instructors/hooks/use-approval-letter-upload/useApprovalLetterUpload';
import { useInstructorActionFeedback } from '@features/instructors/hooks/use-instructor-action-feedback/useInstructorActionFeedback';
import { useInstructorDetailPanel } from '@features/instructors/hooks/use-instructor-detail-panel/useInstructorDetailPanel';
import { useInstructorDisciplineActions } from '@features/instructors/hooks/use-instructor-discipline-actions/useInstructorDisciplineActions';
import * as React from 'react';

import type { InstructorDetailPanelShellProps } from './InstructorDetailPanelShell.types';

import { InstructorRegulationModal } from '../../../regulation-modal/InstructorRegulationModal';
import { InstructorActionFeedbackModal } from '../action-feedback-modal/InstructorActionFeedbackModal';
import { InstructorDetailPanelContent } from '../instructor-detail-panel-content/InstructorDetailPanelContent';
import { InstructorRevokeModal } from '../revoke-modal/InstructorRevokeModal';
import styles from './InstructorDetailPanelShell.module.css';

const InstructorDetailPanelShell = React.memo(
  ({
    onClose,
    instructorId,
    canEdit = false,
  }: Readonly<InstructorDetailPanelShellProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const { instructor, isError, isLoading } = useInstructorDetailPanel({
      instructorId,
    });
    const { selectedOrganization } = useOrganizations();
    const instructorDisplayName = instructor?.displayName ?? '';

    const [regulationsModalOpen, setRegulationsModalOpen] =
      React.useState<boolean>(false);
    const [revokeModalDiscipline, setRevokeModalDiscipline] = React.useState<{
      discipline: InstructorDiscipline;
      programId: string;
    } | null>(null);

    const {
      selectedTarget: uploadLetterTarget,
      uploadedCaseIds,
      isPending: isUploadPending,
      uploadError,
      handleUploadClick,
      handleModalClose: handleUploadLetterClose,
      handleFileChange: handleUploadLetterFileChange,
      handleUpload: handleUploadLetterSubmit,
    } = useApprovalLetterUpload({
      customerId: selectedOrganization?.customerId ?? '',
    });

    const handleUploadLetterOpen = React.useCallback(
      (discipline: InstructorDiscipline): void => {
        handleUploadClick({
          instructorId,
          caseId: discipline.caseId,
          displayName: instructorDisplayName,
        });
      },
      [handleUploadClick, instructorId, instructorDisplayName]
    );

    const {
      handleDisciplineAction,
      isAnyPending,
      pendingAuthorizationId,
      lastActionResult,
      clearLastActionResult,
    } = useInstructorDisciplineActions({
      instructorId,
      customerId: selectedOrganization?.customerId ?? '',
      trainingCenterName: instructor?.primaryTrainingCenter.name ?? '',
      onUploadLetter: handleUploadLetterOpen,
    });

    const { feedbackSnapshot, feedbackModalOpen, closeFeedbackModal } =
      useInstructorActionFeedback({
        lastActionResult,
        clearLastActionResult,
        instructorDisplayName: instructor?.displayName,
      });

    const handleRegulationsModalClose = React.useCallback(
      () => setRegulationsModalOpen(false),
      []
    );

    const handleRevokeOpen = React.useCallback(
      (discipline: InstructorDiscipline, programId: string): void =>
        setRevokeModalDiscipline({ discipline, programId }),
      []
    );

    const handleRevokeConfirm = React.useCallback(
      (description: string): void => {
        if (revokeModalDiscipline) {
          handleDisciplineAction(
            revokeModalDiscipline.discipline,
            revokeModalDiscipline.programId,
            description
          );
        }
        setRevokeModalDiscipline(null);
      },
      [revokeModalDiscipline, handleDisciplineAction]
    );

    const handleClose = React.useCallback((): void => {
      if (isAnyPending) {
        return;
      }
      onClose();
    }, [onClose, isAnyPending]);

    return (
      <>
        <CobaltSidePanel
          size={isMobileOrTablet ? 'large' : 'medium'}
          isOpen
          showCloseButton={true}
          onClose={handleClose}
          title={instructor?.displayName}
          description={
            <p className={`${styles.description} body-02`}>
              By submitting an approval, you acknowledge the following{' '}
              <CobaltLink
                variant="primary"
                aria-label="instructor regulations"
                className={styles.regulationsLink}
                onClick={() => setRegulationsModalOpen(true)}
              >
                regulations
              </CobaltLink>
              .
            </p>
          }
        >
          <InstructorDetailPanelContent
            instructor={instructor}
            isLoading={isLoading}
            isError={isError}
            canEdit={canEdit}
            isAnyPending={isAnyPending}
            pendingAuthorizationId={pendingAuthorizationId}
            uploadedCaseIds={uploadedCaseIds}
            onRevokeOpen={handleRevokeOpen}
            onDisciplineAction={handleDisciplineAction}
          />
        </CobaltSidePanel>
        <InstructorRegulationModal
          isOpen={regulationsModalOpen}
          onClose={handleRegulationsModalClose}
        />
        {revokeModalDiscipline !== null && (
          <InstructorRevokeModal
            isOpen={true}
            onClose={() => setRevokeModalDiscipline(null)}
            onConfirm={handleRevokeConfirm}
            disciplineName={revokeModalDiscipline.discipline.name}
            primaryTrainingCenterName={
              instructor?.primaryTrainingCenter.name ?? ''
            }
            instructorDisplayName={instructor?.displayName ?? ''}
            programId={revokeModalDiscipline.programId}
          />
        )}
        <UploadApprovalLetterModal
          isOpen={uploadLetterTarget !== null}
          displayName={instructorDisplayName}
          isPending={isUploadPending}
          uploadError={uploadError}
          onClose={handleUploadLetterClose}
          onUpload={handleUploadLetterSubmit}
          onFileChange={handleUploadLetterFileChange}
        />
        {feedbackSnapshot &&
          instructor &&
          (feedbackSnapshot.action === 'Approve' ||
            feedbackSnapshot.action === 'Revoke') && (
            <InstructorActionFeedbackModal
              isOpen={feedbackModalOpen}
              instructor={instructor}
              onClose={closeFeedbackModal}
              status={feedbackSnapshot.status}
              disciplineName={feedbackSnapshot.disciplineName}
              programId={feedbackSnapshot.programId}
              action={feedbackSnapshot.action}
            />
          )}
      </>
    );
  }
);

InstructorDetailPanelShell.displayName = 'InstructorDetailPanelShell';
export { InstructorDetailPanelShell };
